import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getPrivacy, postPrivacy } from '@/services/api/profile';

const profilePrivacyQueryKey = (pid: UserPid) => ['profile', 'privacy', pid];

const profilePrivacyQueryOptions = (pid: UserPid) =>
  queryOptions({
    queryFn: getPrivacy,
    queryKey: profilePrivacyQueryKey(pid),
  });

const useProfilePrivacy = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const queryResult = useQuery(profilePrivacyQueryOptions(pid));

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: profilePrivacyQueryKey(pid) });
  }, [pid, queryClient]);

  return {
    ...queryResult,
    invalidate,
  };
};

const useProfilePrivacyMutation = () => {
  return useMutation({
    mutationFn: postPrivacy,
  });
};

export { useProfilePrivacy, useProfilePrivacyMutation };
